import type { Pin } from '@feral/shared/client'
import { tipPlace } from '@feral/ui'
import { Link } from '@tanstack/react-router'
import { type KeyboardEvent, type MouseEvent, useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { SpotStar } from '../favorites/Stars.js'
import { CATEGORY_LABEL } from './common.js'
import { directionsUrl } from './directions.js'

type Spot = NonNullable<NonNullable<Pin['status']>['spot']>
const HOVER_MS = 350
const LEAVE_MS = 250

/**
 * The At a spot tag beside a person (bugs.md): the spot's name, in the status's color, and over it on a
 * computer, or on a tap, a small card with where it goes: the spot's page, directions, its star. The
 * server only sends the spot to those who may see who's at one (FR-WAL-15); without it the tag stays
 * "At a spot". The card is portaled, so a tag inside a row's button never nests its links in it.
 */
export function SpotTag({ spot, className }: { spot: Spot; className?: string }) {
  const tag = useRef<HTMLSpanElement>(null)
  // Where the tag is, and whether a tap opened it (then a tap elsewhere closes it, not the pointer leaving).
  const [open, setOpen] = useState<{ r: DOMRect; tapped: boolean } | null>(null)
  const timer = useRef(0)
  const show = (tapped = false) => {
    window.clearTimeout(timer.current)
    if (tag.current) setOpen({ r: tag.current.getBoundingClientRect(), tapped })
  }
  const hide = (after = LEAVE_MS) => {
    window.clearTimeout(timer.current)
    timer.current = window.setTimeout(() => setOpen(null), after)
  }
  useEffect(() => () => window.clearTimeout(timer.current), [])
  // Scrolling moves the tag out from under its card: close it, as the tooltips do.
  useEffect(() => {
    if (!open) return
    const close = () => setOpen(null)
    window.addEventListener('scroll', close, { capture: true, passive: true })
    return () => window.removeEventListener('scroll', close, { capture: true })
  }, [open])
  // A tap opens it, never the row it sits in.
  const tap = (e: MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()
    if (open?.tapped) setOpen(null)
    else show(true)
  }
  const keys = (e: KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault()
      e.stopPropagation()
      if (open) setOpen(null)
      else show(true)
    } else if (e.key === 'Escape' && open) {
      e.stopPropagation()
      setOpen(null)
    }
  }
  const at = open ? tipPlace(open.r) : null
  return (
    <>
      {/* biome-ignore lint/a11y/useSemanticElements: it sits inside rows that are buttons themselves (a chat header, a tile), where a button can't go */}
      <span
        ref={tag}
        role="button"
        tabIndex={0}
        aria-haspopup="dialog"
        aria-expanded={!!open}
        className={`fm-status-words fm-status-words--at_spot fm-spot-tag${className ? ` ${className}` : ''}`}
        onClick={tap}
        onKeyDown={keys}
        onPointerEnter={(e) => {
          if (e.pointerType !== 'mouse') return
          window.clearTimeout(timer.current)
          timer.current = window.setTimeout(() => show(), HOVER_MS)
        }}
        onPointerLeave={(e) => e.pointerType === 'mouse' && !open?.tapped && hide()}
      >
        <span className="fm-spot-tag__name">At {spot.name}</span>
      </span>
      {open &&
        at &&
        createPortal(
          <>
            {/* Opened by a tap: a tap anywhere else closes it (a phone has no pointer leaving). */}
            {open.tapped && (
              <button
                type="button"
                className="fm-menu__scrim fm-spot-card__scrim"
                aria-label="Close"
                tabIndex={-1}
                onClick={() => setOpen(null)}
              />
            )}
            <div
              role="dialog"
              aria-label={spot.name}
              className={`fm-spot-card ${at.className.replace(/fm-tooltip/g, 'fm-spot-card')}`}
              style={at.style}
              onPointerEnter={() => window.clearTimeout(timer.current)}
              onPointerLeave={(e) => e.pointerType === 'mouse' && !open.tapped && hide()}
            >
              <div className="fm-spot-card__head">
                <span className="fm-spot-card__name">{spot.name}</span>
                <SpotStar id={spot.id} name={spot.name} size={16} />
              </div>
              <span className="fm-sm fm-muted">
                {CATEGORY_LABEL[spot.category as keyof typeof CATEGORY_LABEL] ?? spot.category}
              </span>
              <div className="fm-spot-card__links">
                <Link to="/spots/$id" params={{ id: spot.id }} onClick={() => setOpen(null)}>
                  Open spot
                </Link>
                <a
                  href={directionsUrl(spot.lat, spot.lng, spot.name)}
                  target="_blank"
                  rel="noreferrer"
                  onClick={() => setOpen(null)}
                >
                  Directions
                </a>
                <Link to="/map" search={{ at: `${spot.lat},${spot.lng}` }} onClick={() => setOpen(null)}>
                  On the map
                </Link>
              </div>
            </div>
          </>,
          document.body,
        )}
    </>
  )
}
